import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, Download, Compass } from "lucide-react";
import { MaskLines } from "./Reveal";
import { PROFILE, STATS, CREATIVE_ROLES } from "../../data/content";

export const Hero = () => {
  const ref = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGhost = useTransform(scrollYProgress, [0, 1], [0, -160]);
  // Gentler, later fade on mobile so a small scroll while reading doesn't dim the content.
  const opacity = useTransform(
    scrollYProgress,
    isMobile ? [0, 0.55, 1] : [0, 0.8],
    isMobile ? [1, 1, 0.25] : [1, 0]
  );

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden pt-28 pb-16"
    >
      {/* Ghost oversized word (parallax) */}
      <motion.span
        aria-hidden="true"
        style={{ y: yGhost }}
        className="pointer-events-none select-none absolute -right-10 top-24 md:top-16 font-serif italic text-sand leading-none z-0 text-[34vw] md:text-[24vw]"
      >
        design
      </motion.span>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-[1500px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="mb-6 md:mb-8 max-w-3xl"
        >
          <div className="overline text-ink flex flex-wrap items-center gap-x-3 gap-y-1">
            {PROFILE.roles.map((r, i) => (
              <span key={r} className="flex items-center gap-3">
                {i > 0 && <span className="text-terracotta">/</span>}
                {r}
              </span>
            ))}
          </div>
          <p className="mt-3 text-[0.68rem] uppercase tracking-[0.2em] leading-[2] text-stone/60">
            {CREATIVE_ROLES.map((r, i) => (
              <span key={r}>
                <span className="whitespace-nowrap">
                  {r}
                  {i < CREATIVE_ROLES.length - 1 && <span className="text-terracotta/40"> ·</span>}
                </span>
                {i < CREATIVE_ROLES.length - 1 ? " " : ""}
              </span>
            ))}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-headshot"
          className="hidden lg:block lg:absolute lg:right-6 lg:top-52 z-20 lg:h-72 lg:w-72 xl:h-80 xl:w-80"
        >
          <div className="h-full w-full overflow-hidden rounded-full border-[3px] border-ink/10 bg-sand shadow-2xl ring-1 ring-black/5">
            <img
              src={PROFILE.headshot}
              alt={PROFILE.name}
              className="h-full w-full object-cover"
            />
          </div>
        </motion.div>

        <h1 aria-label="Learning experiences, engineered." className="font-serif tracking-tight leading-[0.86] text-ink text-[11vw] sm:text-[11vw] md:text-[10.5vw] lg:text-[9.5rem]">
          <MaskLines
            lines={["Learning", <>experiences,</>]}
            delay={0.25}
          />
          <span className="block overflow-hidden">
            <motion.span
              className="block italic text-terracotta"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.49 }}
            >
              engineered.
            </motion.span>
          </span>
        </h1>

        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.9 }}
          className="mt-6 md:mt-10 max-w-2xl font-serif italic text-xl md:text-3xl leading-[1.3] tracking-tight text-ink"
        >
          "I'm not a jack of all trades looking for somewhere to fit. I'm a
          <span className="text-terracotta"> specialist with range</span> who knows exactly where to
          focus it."
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-8 md:mt-14 flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-20"
        >
          <div className="lg:max-w-xl">
            <div className="flex items-start gap-5">
              <div
                data-testid="hero-headshot-mobile"
                className="lg:hidden shrink-0 h-24 w-24 sm:h-28 sm:w-28 overflow-hidden rounded-full border-2 border-ink/10 bg-sand shadow-lg ring-1 ring-black/5"
              >
                <img src={PROFILE.headshot} alt={PROFILE.name} className="h-full w-full object-cover" />
              </div>
              <p className="text-base md:text-xl text-stone leading-relaxed font-light">
                I'm <span className="text-ink font-medium">{PROFILE.name}</span>. I turn complex
                enterprise systems into training people actually understand, 13 years in Instructional
                Design and ERP Training and 20 in graphic design. Beyond the classroom I'm also a photographer and
                videographer, web and interior designer, and a licensed REALTOR&reg; with a
                multimillion-dollar real estate flip portfolio.
              </p>
            </div>

            <div
              data-testid="hero-explore-note"
              className="mt-6 flex items-start gap-3 rounded-2xl border border-terracotta/40 bg-sand px-5 py-4"
            >
              <Compass size={20} className="mt-0.5 shrink-0 text-terracotta" />
              <div>
                <p className="text-sm md:text-base font-medium text-ink leading-snug">
                  New here? Use the menu to explore each area of my work.
                </p>
                <p className="mt-1.5 text-sm text-stone leading-relaxed">
                  This home page is just the introduction. Every discipline has its own page:
                  Instructional Design, Photography &amp; Videography, Web &amp; Graphic Design, and
                  Interior Design. On a phone, tap{" "}
                  <span className="text-ink font-medium">"Menu"</span> in the top corner to switch
                  between them.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Link
              to="/instructional-design"
              data-testid="hero-view-work"
              className="group inline-flex items-center gap-3 rounded-full bg-ink text-paper pl-7 pr-3 py-3 text-sm uppercase tracking-[0.14em] hover:bg-terracotta transition-colors duration-300"
            >
              View the work
              <span className="grid place-items-center w-8 h-8 rounded-full bg-paper text-ink group-hover:rotate-45 transition-transform duration-300">
                <ArrowDownRight size={16} />
              </span>
            </Link>
            <a
              href={PROFILE.resume}
              download
              data-testid="hero-resume-download"
              className="inline-flex items-center gap-2 rounded-full border border-ink/25 text-ink px-6 py-3 text-sm uppercase tracking-[0.14em] hover:border-ink hover:bg-ink hover:text-paper transition-colors duration-300"
            >
              <Download size={15} /> Résumé
            </a>
          </div>
        </motion.div>
      </motion.div>

      {/* Stat strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="relative z-10 mx-auto w-full max-w-[1500px] px-6 md:px-12 mt-16 md:mt-24"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-line">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className={`py-6 md:py-8 ${i !== 0 ? "md:border-l md:border-line md:pl-8" : ""} ${
                i % 2 !== 0 ? "border-l border-line pl-6 md:pl-8" : ""
              }`}
            >
              <div className="font-serif text-4xl md:text-5xl tracking-tight text-ink">{s.value}</div>
              <div className="mt-1 text-xs md:text-sm text-stone leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
